export type WorkforceStatus = 'active' | 'completed' | 'terminated' | 'on_hold' | 'on_bench';

export type BenchReason = 'between_assignments' | 'just_hired' | 'training' | 'project_ended' | 'client_cancelled' | 'awaiting_start' | 'other';
export type BillingType = 'daily' | 'hourly' | 'fixed' | 'monthly';
export type LocationType = 'on-site' | 'remote' | 'hybrid';
export type ContractType = 'freelance' | 'employee' | 'contractor' | 'interim' | 'fixed-term';

export type StatusChangeReason = 'project_ended' | 'client_cancelled' | 'contract_expired' | 'consultant_resigned' | 'performance' | 'budget_cut' | 'restructuring' | 'mutual_agreement' | 'other';

type WorkforceCustomFieldsValues = Record<string, any>;

export interface WorkforceComment {
	id: string;
	workforce_id: string;
	created_at: string;
	updated_at: string;
	content: string;
	created_by: string;
	created_by_uuid: string | null;
	migration?: boolean;
	profiles?: {
		first_name: string | null;
		last_name: string | null;
	};
}

export interface Workforce {
	id: string;
	organization_uuid: string;

	// Candidate link
	candidate_id: string;
	candidate?: {
		id: string;
		name: string;
		email?: string;
		phone?: string;
		function?: string;
		skills?: string[];
	};

	// Job link (optional)
	job_id?: string | null;
	job?: {
		id: string;
		title: string;
		company?: {
			id: string;
			name: string;
		};
	};

	// External job details (when no job_id)
	external_job_title?: string | null;
	external_company_name?: string | null;
	external_company_contact?: string | null;
	external_job_description?: string | null;

	// Assignment dates
	start_date: string;
	end_date?: string | null;
	expected_end_date?: string | null;

	// Status
	status: WorkforceStatus;
	status_change_reason?: StatusChangeReason | null;
	status_change_note?: string | null;

	// Financial - Cost (what we pay the consultant)
	purchase_price_daily?: number | null;
	purchase_price_hourly?: number | null;
	// Financial - Revenue (what we charge the client)
	selling_price_daily?: number | null;
	selling_price_hourly?: number | null;
	// Legacy fields (deprecated - use purchase/selling prices)
	daily_rate?: number | null;
	hourly_rate?: number | null;
	currency?: string;
	billing_type?: BillingType | null;

	// Work arrangement
	location?: string | null;
	location_type?: LocationType | null;
	work_percentage?: number | null;

	// Contract
	contract_type?: ContractType | null;
	contract_reference?: string | null;

	// Administrative
	assigned_recruiter?: string | null;
	assigned_recruiter_profile?: {
		id: string;
		name: string;
		first_name?: string;
		last_name?: string;
	};
	notes?: string | null;
	custom_fields?: WorkforceCustomFieldsValues;

	// Bench flag
	is_bench?: boolean;

	// Bench-specific fields
	bench_reason?: BenchReason | null;
	bench_daily_cost?: number | null;
	previous_workforce_id?: string | null;

	// Timestamps
	created_at: string;
	updated_at?: string;
}

export interface WorkforceFormData {
	candidate_id: string;
	job_id?: string | null;
	external_job_title?: string;
	external_company_name?: string;
	external_company_contact?: string;
	external_job_description?: string;
	start_date: string;
	end_date?: string;
	expected_end_date?: string;
	status: WorkforceStatus;
	// Financial - Cost (what we pay)
	purchase_price_daily?: string;
	purchase_price_hourly?: string;
	// Financial - Revenue (what we charge)
	selling_price_daily?: string;
	selling_price_hourly?: string;
	// Legacy fields (deprecated)
	daily_rate?: string;
	hourly_rate?: string;
	currency: string;
	billing_type?: BillingType;
	location?: string;
	location_type?: LocationType;
	work_percentage?: string;
	contract_type?: ContractType;
	contract_reference?: string;
	assigned_recruiter?: string;
	notes?: string;
	// Bench-specific fields
	bench_reason?: BenchReason;
	bench_daily_cost?: string;
}

// Helper function to calculate margin percentage
export function calculateMargin(purchasePrice: number | null | undefined, sellingPrice: number | null | undefined): number | null {
	if (!purchasePrice || !sellingPrice || sellingPrice === 0) return null;
	return ((sellingPrice - purchasePrice) / sellingPrice) * 100;
}

// Helper function to check if assignment is ending soon (within X days)
export function isEndingSoon(expectedEndDate: string | null | undefined, daysThreshold: number = 30): boolean {
	if (!expectedEndDate) return false;
	const endDate = new Date(expectedEndDate);
	const today = new Date();
	const diffTime = endDate.getTime() - today.getTime();
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
	return diffDays >= 0 && diffDays <= daysThreshold;
}

// Helper function to get days until end
export function getDaysUntilEnd(expectedEndDate: string | null | undefined): number | null {
	if (!expectedEndDate) return null;
	const endDate = new Date(expectedEndDate);
	const today = new Date();
	const diffTime = endDate.getTime() - today.getTime();
	return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// Status options for dropdowns
export const WORKFORCE_STATUSES: { value: WorkforceStatus; label: string }[] = [
	{ value: 'active', label: 'Active' },
	{ value: 'on_hold', label: 'On Hold' },
	{ value: 'completed', label: 'Completed' },
	{ value: 'terminated', label: 'Terminated' },
];

export const BILLING_TYPES: { value: BillingType; label: string }[] = [
	{ value: 'daily', label: 'Daily' },
	{ value: 'hourly', label: 'Hourly' },
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'fixed', label: 'Fixed Price' },
];

export const LOCATION_TYPES: { value: LocationType; label: string }[] = [
	{ value: 'on-site', label: 'On-site' },
	{ value: 'remote', label: 'Remote' },
	{ value: 'hybrid', label: 'Hybrid' },
];

export const CONTRACT_TYPES: { value: ContractType; label: string }[] = [
	{ value: 'freelance', label: 'Freelance' },
	{ value: 'employee', label: 'Employee' },
	{ value: 'contractor', label: 'Contractor' },
	{ value: 'interim', label: 'Interim' },
	{ value: 'fixed-term', label: 'Fixed-term' },
];

export const CURRENCY_OPTIONS = ['EUR', 'USD', 'GBP', 'CHF'];

export const STATUS_CHANGE_REASONS: { value: StatusChangeReason; label: string }[] = [
	{ value: 'project_ended', label: 'Project ended' },
	{ value: 'client_cancelled', label: 'Client cancelled' },
	{ value: 'contract_expired', label: 'Contract expired' },
	{ value: 'consultant_resigned', label: 'Consultant resigned' },
	{ value: 'performance', label: 'Performance issue' },
	{ value: 'budget_cut', label: 'Budget cut' },
	{ value: 'restructuring', label: 'Restructuring' },
	{ value: 'mutual_agreement', label: 'Mutual agreement' },
	{ value: 'other', label: 'Other' },
];

export const BENCH_REASONS: { value: BenchReason; label: string }[] = [
	{ value: 'between_assignments', label: 'Between assignments' },
	{ value: 'just_hired', label: 'Just hired' },
	{ value: 'training', label: 'Training' },
	{ value: 'project_ended', label: 'Project ended early' },
	{ value: 'client_cancelled', label: 'Client cancelled' },
	{ value: 'awaiting_start', label: 'Awaiting start date' },
	{ value: 'other', label: 'Other' },
];
